import { useEffect, useState } from 'react';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

// Results table for the validated SPY-confirmed contract RSI(7)+VWAP signal
// (backend/handlers/rsi_vwap_study.go documents the exact rule set). One
// fixed, already-studied signal - no strategy parameters here, just a date
// picker, since that's what makes this different from the generic backtest
// tool this page used to have.
// Window options, in trading sessions. 605 is the full span the offline
// study covered (2024-03-01 onward), so "All" here means the same population
// those results were measured on.
const WINDOWS = [
  { days: 45, label: '45 sessions' },
  { days: 90, label: '90 sessions' },
  { days: 180, label: '180 sessions' },
  { days: 365, label: '365 sessions' },
  { days: 605, label: 'All (since Mar 2024)' },
];

function fmtDateLabel(ds) {
  // ds is YYYY-MM-DD; noon avoids any TZ-rollover on the label.
  return new Date(ds + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function RSIVWAPStudyTable({ onSelect }) {
  // Only dates that actually produced a signal are selectable - the scan
  // below decides which those are, so an empty day can't be picked at all.
  const [windowDays, setWindowDays] = useState(180);
  const [dates, setDates] = useState(null); // [{date,count}] | null while scanning
  const [scanInfo, setScanInfo] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [date, setDate] = useState(null);

  const [data, setData] = useState(null); // { date, note, trades } | null while loading
  const [error, setError] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  // The scan is progressive: the backend answers immediately with whatever
  // it has already computed and fills the rest in the background, so poll
  // until nothing is pending. Widening the window re-polls the same way.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    setDates(null);
    setScanInfo(null);
    setScanError(null);

    const poll = async () => {
      try {
        const token = await getValidToken();
        if (!token || cancelled) return;
        const res = await fetch(`${API_URL}/api/rsi-vwap-study/dates?days=${windowDays}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        setDates(json.dates || []);
        setScanInfo(json);
        setDate(prev => {
          if (prev && json.dates?.some(d => d.date === prev)) return prev;
          return json.dates?.length ? json.dates[0].date : null;
        });
        if (json.pending > 0) timer = setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setScanError(e.message || 'scan failed');
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [windowDays]);

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const token = await getValidToken();
        if (!token || cancelled) return;
        const res = await fetch(`${API_URL}/api/rsi-vwap-study?date=${date}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || 'failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const select = (t) => {
    setSelectedSymbol(t.symbol);
    onSelect?.({ symbol: t.symbol });
  };

  const trades = data?.trades || [];

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: '#e5e7eb' }}>RSI(7) + VWAP signal — trade results</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            SPY RSI(7) 80/20 confirmation (≤3 bars) + contract RSI(7) crosses up 25 above its SMA(3) +
            close above VWAP (same or next bar) + stop 5% below the pre-entry bar's open, 30-min max hold.
            Click a row to chart that contract at 5-minutes.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <select
              value={windowDays}
              onChange={e => setWindowDays(Number(e.target.value))}
              title="How far back to scan for signals"
              className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500"
              style={{ backgroundColor: '#1f2937', color: '#9ca3af', borderColor: '#374151' }}
            >
              {WINDOWS.map(w => <option key={w.days} value={w.days}>{w.label}</option>)}
            </select>
            {dates !== null && dates.length > 0 && (
              <select
                value={date || ''}
                onChange={e => setDate(e.target.value)}
                className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500"
                style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151' }}
              >
                {dates.map(d => (
                  <option key={d.date} value={d.date}>
                    {fmtDateLabel(d.date)} — {d.count} trade{d.count === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            )}
          </div>
          {scanError && <span className="text-xs" style={{ color: '#ef4444' }}>Scan failed: {scanError}</span>}
          {!scanError && scanInfo && scanInfo.pending > 0 && (
            <span className="text-xs" style={{ color: '#f59e0b' }}>
              Scanning… {scanInfo.done}/{scanInfo.sessions} sessions ({dates?.length || 0} with signals so far)
            </span>
          )}
          {!scanError && scanInfo && scanInfo.pending === 0 && (
            <span className="text-xs text-gray-600">
              {dates?.length || 0} of {scanInfo.sessions} sessions had a signal · {scanInfo.from} → {scanInfo.to}
            </span>
          )}
          {!scanError && !scanInfo && <span className="text-xs text-gray-500">Starting scan…</span>}
        </div>
      </div>

      {error && (
        <div className="text-center py-8 text-sm" style={{ color: '#ef4444' }}>Error: {error}</div>
      )}

      {dates !== null && dates.length === 0 && !scanError && scanInfo?.pending === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">
          No session in the last {scanInfo.sessions} produced a signal.
        </div>
      )}

      {!error && date && data === null && (
        <div className="space-y-2 mt-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-7 bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {!error && data !== null && trades.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">{data.note || 'No qualifying signals on this date'}</div>
      )}

      {!error && trades.length > 0 && (
        <div className="overflow-auto mt-3" style={{ maxHeight: 420 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Strike</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Signal</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Entry</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Stop</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Max Gain</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Max Loss</th>
                <th className="text-center pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">1st</th>
                <th className="text-right pb-2 text-xs font-medium text-gray-400 uppercase tracking-wider">Result @30m</th>
              </tr>
            </thead>
            <tbody>
              {trades.map(t => {
                const isCall = t.side === 'CALL';
                const gColor = t.maxGainPct >= 0 ? '#22c55e' : '#6b7280';
                const lColor = t.maxLossPct <= 0 ? '#ef4444' : '#6b7280';
                const finalColor = t.finalPct >= 0 ? '#22c55e' : '#ef4444';
                const selected = selectedSymbol === t.symbol;
                return (
                  <tr
                    key={t.symbol + t.entryTime}
                    onClick={() => select(t)}
                    className="border-t border-gray-800 hover:bg-gray-800/40 cursor-pointer"
                    style={selected ? { backgroundColor: 'rgba(96,165,250,0.10)' } : undefined}
                  >
                    <td className="py-1.5 pr-3">
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: isCall ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isCall ? '#22c55e' : '#ef4444',
                        }}
                      >{t.side}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: '#e5e7eb' }}>{t.strike}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">{t.sigTime}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">{t.entryTime}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: '#60a5fa' }}>${t.entryPrice.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500">${t.stopPrice.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: gColor }}>
                      +{t.maxGainPct.toFixed(1)}% <span className="text-gray-600">@{t.maxGainMin}m</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: lColor }}>
                      {t.maxLossPct.toFixed(1)}% <span className="text-gray-600">@{t.maxLossMin}m</span>
                    </td>
                    <td className="py-1.5 pr-3 text-center text-xs" style={{ color: t.gainFirst ? '#22c55e' : '#ef4444' }}>
                      {t.gainFirst ? 'Gain' : 'Loss'}
                    </td>
                    <td className="py-1.5 text-right tabular-nums" style={{ color: finalColor }}>
                      {t.finalPct >= 0 ? '+' : ''}{t.finalPct.toFixed(1)}%
                      {t.stopped
                        ? <span className="text-gray-600"> stopped @{t.stopTimeMin}m</span>
                        : <span className="text-gray-600"> @{t.finalTimeMin}m</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
